<template>
  <ul v-if="presets">
    <template
      v-for="(preset, index) in presets"
      :key="preset.data._uid"
    >
      <PbLink
        v-slot="{
          navigate, href, isActive,
        }"
        custom
        :to="preset.link"
      >
        <!-- Other available props from v-slot: href, route -->
        <PresetItem
          :preset="preset"
          :href="href"
          :active="isActive"

          :loading="loading"

          :start="ranges[index].start"
          :end="ranges[index].end"
          :size="ranges[index].size"
          :start-can-grow="ranges[index].startCanGrow"
          :start-can-shrink="ranges[index].startCanShrink"
          :start-can-change="ranges[index].startCanChange"
          :end-can-grow="ranges[index].endCanGrow"
          :end-can-shrink="ranges[index].endCanShrink"
          :end-can-change="ranges[index].endCanChange"

          :drag-drop-context="dragDropContext"

          @update-start="updateStart"
          @update-end="updateEnd"

          @navigate="navigate"
          @submit="submitRanges"
        />
      </PbLink>
    </template>
  </ul>
</template>

<script setup>
import {
  ref,
  watch,
} from 'vue'

import useIsGlobal from '@/composables/useIsGlobal'
import PresetItem from '@/components/PresetItem/PresetItem.vue'

import usePresetRangeStore from '@/stores/presetRange.store'
import { useDragDropContext } from '@/composables/useDragDrop'
import PbLink from '@/components/PbLink.vue'
import { usePresetRangeStatus, usePresets } from '@/composables/useProfile'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import useDmxRanges, { getSize } from '@/composables/useDmxRanges'

const { isGlobal } = useIsGlobal()

const presetRangeStore = usePresetRangeStore()

const loading = usePresetRangeStatus().pending
const sizes = ref([])
const ranges = useDmxRanges(sizes)
const lastDistribution = ref([])
// Get the list of presets
const presets = usePresets()

// Update the ranges when the presets change
watch(presets, () => {
  if (presets.value) {
    sizes.value = presets.value.map((preset) => getSize({
      start: preset.data.dmx_start,
      end: preset.data.dmx_end,
    }))
  } else {
    sizes.value = []
  }
  // Cache the distribution
  lastDistribution.value = [...sizes.value]
}, { immediate: true })

const movePreset = (presetUid, destinationOrder) => {
  if (isGlobal.value) {
    return presetRangeStore.moveGlobalPreset({ _uid: presetUid, _order: destinationOrder })
  }
  return presetRangeStore.moveLocalPreset({ _uid: presetUid, _order: destinationOrder })
}

const dragDropContext = useDragDropContext({
  onDrop(draggedPresetData, dropzoneData) {
    const { before, after, preset: dropzonePreset } = dropzoneData
    if (draggedPresetData.data._uid !== dropzonePreset.data._uid) {
      // If the preset is not moving to it's own position
      const sourceOrder = draggedPresetData.data._order
      let destinationOrder = dropzonePreset.data._order
      if (sourceOrder < destinationOrder && before) {
        // If the preset we're moving was before the preset we're moving to
        // and we dropped before the preset we're moving to...
        // Remove 1 to the destination order, to take into account all orders shifting
        // because of the moving preset
        destinationOrder -= 1
      }
      if (sourceOrder > destinationOrder && after) {
        // If the preset we're moving was after the preset we're moving to
        // and we dropped after the preset we're moving to...
        // Add 1 to the order, to take into account all orders shifting
        // because of the moving preset
        destinationOrder += 1
      }
      if (destinationOrder !== sourceOrder) {
        dev.log(`Moving preset ${draggedPresetData.data.name} to order ${destinationOrder}`)
        movePreset(draggedPresetData.data._uid, destinationOrder)
      }
    }
  },
})

// Handle distribution
const distribute = (distribution) => {
  if (isGlobal.value) {
    return presetRangeStore.distributeGlobal({ distribution })
  }
  return presetRangeStore.distributeLocal({ distribution })
}

const updateStart = (preset, start) => {
  if (!loading.value) {
    const range = ranges.value[preset.data._order]
    range.moveStartTo(start)
  }
}

const updateEnd = (preset, end) => {
  if (!loading.value) {
    const range = ranges.value[preset.data._order]
    range.moveEndTo(end)
  }
}

const submitRanges = () => {
  if (!loading.value) {
    const prevSizes = lastDistribution.value
    const newSizes = sizes.value
    // Get the new distribution of the ranges
    if (newSizes.join() !== prevSizes.join()) {
      // If the distribution changed
      // Cache the distribution
      lastDistribution.value = newSizes
      // And distribute
      return distribute(newSizes)
    }
  }
  return undefined
}
</script>

<style lang="scss" src="./PresetRange.scss"></style>
